import {
  DxfParser,
  EntityName,
  TableName,
  TableKey,
} from '@frontend/dxfparser';
import type {
  DxfEntity,
  IArcEntity,
  IBlock,
  ICircleEntity,
  IDimensionEntity,
  IDxf,
  IEllipseEntity,
  IInsertEntity,
  ILayer,
  ILayersTable,
  ILeaderEntity,
  ILineEntity,
  ILineType,
  ILwpolylineEntity,
  IMLineEntity,
  IMtextEntity,
  IPoint,
  IPointEntity,
  IPolylineEntity,
  ISolidEntity,
  ISplineEntity,
  IStyle,
  ITextEntity,
} from '@frontend/dxfparser';

//eslint-disable-next-line no-restricted-imports
import {
  ENTITY_TYPES,
  EXPORT_LAYER_KEYS,
  PART_TYPES,
  UNITS,
} from '../../constants';
//eslint-disable-next-line no-restricted-imports
import type {
  Matrix4Json,
  PointJson,
  QuaternionJson,
  Vector3Json,
} from '../../contracts/basic';
//eslint-disable-next-line no-restricted-imports
import type {
  TessJson,
  FaceJson,
  EdgeJson,
  VertexJson,
  TextJson,
  RefJson,
} from '../../contracts/tess';
//eslint-disable-next-line no-restricted-imports
import {
  angleTo,
  bspline,
  cross,
  degToRad,
  halfPi,
  identityMatrix4,
  matrix4Compose,
  matrix4DecomposeScale,
  matrix4Multiply,
  matrix4Vec3Multiply,
  norm,
  normalize,
  quaternionFromAxisAngle,
  scaleVec,
  twoPi,
  vecAdd,
  vecSub,
} from '../../geom-utils';
import type { Body } from '../body';
import type { Part } from '../part';
import type { MetaDataType } from '../step/STEPGeometry';
//eslint-disable-next-line no-restricted-imports
import { constructEntityName } from '../step/utils';
import { parseDxfMTextContent } from './utils/mtext';
import type { DxfMTextContentElement } from './utils/mtext';
import { parseDxfTextContent } from './utils/text';

type Point2Json = [number, number];

type TessAccumulator = {
  faces: FaceJson[];
  edges: EdgeJson[];
  vertices: VertexJson[];
  texts: TextJson[];
  refs: RefJson[];
};

type ActiveStyle = {
  textAlign: TextJson['textAlign'];
  textHeight: number;
  fontFamily: [string, number, number][]; //fontFamilyName, text start index, end index
  scale?: number;
};

export type TessIndexToDXFEntity = Map<number, [MetaDataType, DxfEntity]>;

const xAxis: Vector3Json = [1, 0, 0];
const yAxis: Vector3Json = [0, 1, 0];
const zAxis: Vector3Json = [0, 0, 1];
//const defaultExtrustionDir = {x: 0, y: 0, z: 1};
const noRotation: QuaternionJson = [0, 0, 0, 1];
const defaultScale: Vector3Json = [1, 1, 1];
const polorCapBounds = 1 / 64;

//* It is a singleton created with anonymous class expression
export const dxfService = new (class DXFService {
  #dxfByPartFileUuid = new Map<
    string,
    { dxf: IDxf; map: TessIndexToDXFEntity }
  >();
  #parser = new DxfParser();
  #lastIndex = 0;

  public clear(): void {
    this.#dxfByPartFileUuid.clear();
  }

  public delete(partFileUuid: string): boolean {
    return this.#dxfByPartFileUuid.delete(partFileUuid);
  }

  public updateDxfForPart(part: Part<PART_TYPES.DXF>): IDxf | undefined {
    //get the body's part... and partFileUuid, and then get the original IDxf...
    //clone IDxf, and modify each entity in IDxf... and then serialize to Blob
    const { id } = part;
    const { dxf, map } = this.#dxfByPartFileUuid.get(id as string) ?? {};
    if (dxf && map) {
      const layersTable = this.#getLayersTable(dxf);
      //Update layer for each entity
      //Note: this mutates entities in dxf in the #dxfByPartFileUuid, but that is okay
      //because we don't need the original dxf. We only need the last mutated state based on
      //edits that were made to `Part`.
      for (const [index, [type, entity]] of map.entries()) {
        const { layer } = part.getEntityMeta(index, type) || {};
        if (layer) {
          this.#addOrUpdateLayerTable(entity, layersTable, layer);
        }
      }
      layersTable.maxEntities = Array.from(
        Object.keys(layersTable[TableKey.Layers]),
      ).length;
      return dxf;
    }
  }

  public serializePart(part: Part<PART_TYPES.DXF>): Blob | undefined {
    const dxf = this.updateDxfForPart(part);
    if (dxf) {
      return this.#parser.serializeToBlob(dxf);
    }
  }

  public updateDxfForBody(body: Body<PART_TYPES.DXF>): IDxf | undefined {
    //get the body's part... and partFileUuid, and then get the original IDxf...
    //clone IDxf, and modify each entity in IDxf... and then serialize to Blob
    const {
      id: bodyId,
      part: { id: partId },
      obj,
    } = body;
    const { dxf, map } = this.#dxfByPartFileUuid.get(partId as string) ?? {};
    if (dxf && map) {
      const layersTable = this.#getLayersTable(dxf);
      //Update layer for each entity
      //Note: this mutates entities in dxf in the #dxfByPartFileUuid, but that is okay
      //because we don't need the original dxf. We only need the last mutated state based on
      //edits that were made to `STEPBody vectorStyleByNameMap`.
      for (const [ind, [type, entity]] of map.entries()) {
        const { layer } =
          obj.vectorStyleByNameMap.get(
            constructEntityName(bodyId ?? 'undefined', type, ind),
          ) || {};
        // TODO: handle only "saving" cut layer for milestone 1
        if (layer && EXPORT_LAYER_KEYS.includes(layer)) {
          this.#addOrUpdateLayerTable(entity, layersTable, layer);
        }
      }
      layersTable.maxEntities = Array.from(
        Object.keys(layersTable[TableKey.Layers]),
      ).length;
      return dxf;
    }
  }

  public serializeBody(body: Body<PART_TYPES.DXF>): Blob | undefined {
    const dxf = this.updateDxfForBody(body);
    if (dxf) {
      return this.#parser.serializeToBlob(dxf);
    }
  }

  public serializeBodyForLayer(
    body: Body<PART_TYPES.DXF>,
    layer: string = EXPORT_LAYER_KEYS[0],
  ): Blob | undefined {
    const originalDxf = this.updateDxfForBody(body);
    if (originalDxf) {
      const { header, entities, blocks, tables, classes, objects } =
        originalDxf;
      const filteredHandles: Set<string> = new Set();
      const filteredEntities = (entities ?? []).filter(
        (entity) => entity.layer === layer,
      );
      const filteredBlocks = Object.fromEntries(
        Object.entries(blocks ?? {}).filter(([, block]) => {
          const keep =
            !block.entities ||
            block.entities.some(
              (entity) =>
                entity.layer === layer ||
                !filteredHandles.has(block.ownerHandle),
            );
          if (!keep && typeof block.handle === 'string') {
            filteredHandles.add(block.handle);
          }
          return keep;
        }),
      );

      const { [TableName.Layer]: layersTable, ...otherTables } = tables ?? {};
      const { [TableKey.Layers]: layers, ...baseLayerItems } =
        layersTable ?? {};
      const { [layer]: layerTable } = layers ?? {};

      const dxf: IDxf = {
        ...(header && { header }),
        ...(blocks && { blocks: filteredBlocks }),
        ...(tables && {
          tables: {
            [TableName.Layer]: {
              ...baseLayerItems,
              maxEntities: 1, //only 1 layer is being exported
              [TableKey.Layers]: {
                [layer]: layerTable, //only include the specified `layer` table (ie 'Paperless-Parts-Cut')
              },
            },
            ...otherTables,
          },
        }),
        ...(classes && { classes }),
        ...(objects && { objects }),
        entities: filteredEntities,
      };
      return this.#parser.serializeToBlob(dxf);
    }
  }

  //#getNextHandle() is slow. It searches all existing handles for the max handle
  //This does not generate a suitable handle... so don't use
  //#getNextHandle(dxf: IDxf): string {
  //  let maxHandle = 0;
  //  for (const { handle } of Object.values(dxf.tables ?? {})) {
  //    if (typeof handle === 'string') {
  //      const h = parseInt(handle, 16);
  //      if (maxHandle < h) {
  //        maxHandle = h;
  //      }
  //    }
  //  }
  //  for (const block of Object.values(dxf.blocks ?? {})) {
  //    const { handle, entities } = block;
  //    if (typeof handle === 'string') {
  //      const h = parseInt(handle, 16);
  //      if (maxHandle < h) {
  //        maxHandle = h;
  //      }
  //    }
  //    for (const { handle } of entities ?? []) {
  //      if (typeof handle === 'string') {
  //        const h = parseInt(handle, 16);
  //        if (maxHandle < h) {
  //          maxHandle = h;
  //        }
  //      }
  //    }
  //  }
  //  for (const { handle } of dxf.entities ?? []) {
  //    if (typeof handle === 'string') {
  //      const h = parseInt(handle, 16);
  //      if (maxHandle < h) {
  //        maxHandle = h;
  //      }
  //    }
  //  }
  //  return Number(maxHandle + 1).toString(16);
  //}

  #getLayersTable(dxf: IDxf): ILayersTable {
    if (!dxf.tables) {
      dxf.tables = {};
    }
    if (!dxf.tables[TableName.Layer]) {
      dxf.tables[TableName.Layer] = {
        [TableKey.Layers]: {},
        ownerHandle: '0', //ownerHandle of a table is always 0
        maxEntities: 0,
      };
    }
    return dxf.tables[TableName.Layer];
  }

  #addOrUpdateLayerTable(
    entity: DxfEntity,
    layersTable: ILayersTable,
    layerName: string,
  ) {
    const layers = layersTable[TableKey.Layers];
    const originalLayer = layers[entity.layer];
    entity.layer = layerName;
    if (!layers[layerName]) {
      const color = originalLayer.color ?? entity.color;
      const colorIndex = originalLayer.colorIndex ?? entity.colorIndex;
      const lineType = originalLayer.lineType ?? entity.lineType;
      const lineWeight = originalLayer.lineWeight ?? entity.lineweight;
      const plotStyleId = originalLayer.plotStyleId;
      const materialId = originalLayer.materialId;

      layers[layerName] = {
        //IMPORTANT: subMarker1 and subMarker2 must be inserted right after ownerHandle and before name
        //otherwise the dxf won't serialize to propery DXF that eDrawings can read. (Other DXF readers may be fine, but not eDrawings)
        subMarker1: 'AcDbSymbolTableRecord',
        subMarker2: 'AcDbLayerTableRecord',
        name: layerName,
        visible: true,
        standardFlags: 0, //code 70
        ...(colorIndex && { colorIndex }), //code 62
        ...(lineType && { lineType }), //code 6
        ...(lineWeight && { lineWeight }), //code 370
        ...(plotStyleId && { plotStyleId }), //code 390
        ...(color && { color }), //code 420
        ...(materialId && { materialId }), //code 347
      };
    }
  }

  //currently returns TessJson
  //Will update to return {part, body}
  public importDxfContent(partFileUuid: string, content: string): TessJson {
    const dxf: IDxf = this.#parser.parse(content) ?? {};
    this.#lastIndex = 0;

    const tess: TessJson & Required<Pick<TessJson, 'Texts' | 'Refs'>> = {
      Faces: [],
      Edges: [],
      Vertexes: [],
      Texts: [],
      Refs: [],
      Type: PART_TYPES.DXF,
    };

    const map: TessIndexToDXFEntity = new Map();

    for (const entity of dxf.entities ?? []) {
      const { faces, edges, vertices, texts, refs } = this.#addEntity(
        entity,
        dxf,
      );
      //Note: From this context, this.#addEntities() returns at most 1 face, edge, vertex, text or ref
      for (const face of faces ?? []) {
        tess.Faces.push(face);
        map.set(face.ind, [ENTITY_TYPES.FACE, entity]);
      }
      for (const edge of edges ?? []) {
        tess.Edges.push(edge);
        map.set(edge.ind, [ENTITY_TYPES.EDGE, entity]);
      }
      for (const vertex of vertices ?? []) {
        tess.Vertexes.push(vertex);
        map.set(vertex.ind, [ENTITY_TYPES.VERTEX, entity]);
      }
      for (const text of texts ?? []) {
        tess.Texts.push(text);
        map.set(text.ind, [ENTITY_TYPES.TEXT, entity]);
      }
      for (const ref of refs ?? []) {
        tess.Refs.push(ref);
        map.set(ref.ind, [ENTITY_TYPES.REF, entity]);
      }
    }

    this.#dxfByPartFileUuid.set(partFileUuid, { dxf, map });
    return tess;
  }

  public getScale(partFileUuid: string) {
    const obj = this.#dxfByPartFileUuid.get(partFileUuid);
    if (obj?.dxf?.header?.['$DIMSCALE']) {
      return obj.dxf.header['$DIMSCALE'] as number;
    }
  }

  //Setting scale like this does not work with ezDXF and HOOPs.
  //Likely all geometry needs to be scaled with transform. And $DIMSCALE is not for this purpose.
  //public setScale(partFileUuid: string, scale: number) {
  //  const obj = this.#dxfByPartFileUuid.get(partFileUuid);
  //  if (obj?.dxf?.header) {
  //    //Set the plot scale to inverse of intended plot scale
  //    obj.dxf.header['$DIMSCALE'] = 1 / scale / 72000;
  //  }
  //}

  public getUnits(partFileUuid: string): UNITS | undefined {
    const obj = this.#dxfByPartFileUuid.get(partFileUuid);
    if (obj?.dxf?.header?.['$INSUNITS']) {
      if (obj.dxf.header['$INSUNITS'] === 1) return UNITS.IN;
      if (obj.dxf.header['$INSUNITS'] === 4) return UNITS.MM;
    }
  }
  public setUnits(partFileUuid: string, units: UNITS) {
    const obj = this.#dxfByPartFileUuid.get(partFileUuid);
    if (obj?.dxf?.header) {
      if (units === UNITS.IN) {
        obj.dxf.header['$INSUNITS'] = 1;
      }
      if (units === UNITS.MM) {
        obj.dxf.header['$INSUNITS'] = 4;
      }
    }
  }

  //nextIndex: a factory for `index` of face, edge or vertex
  #nextIndex(): number {
    return this.#lastIndex++;
  }

  #addEntity<T extends DxfEntity>(
    entity: T,
    dxf: IDxf,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): Partial<TessAccumulator> {
    const lineTypes =
      dxf?.tables &&
      dxf.tables[TableName.LineType] &&
      dxf.tables[TableName.LineType][TableKey.LineTypes];
    const layers =
      dxf?.tables &&
      dxf.tables[TableName.Layer] &&
      dxf.tables[TableName.Layer][TableKey.Layers];

    const styles =
      dxf?.tables &&
      dxf.tables[TableName.Style] &&
      dxf.tables[TableName.Style][TableKey.Styles];

    //See this.#addInsert. The rotation for insert is opposite of what I expected.
    //Most of the spec for DXF says angles are measured CCW (which is positive about extrude direction),
    //but for some reason, it is actually negative. I looked at the .dxf file, and found the $ANGDIR and it is positive
    //in the cases I looked at. But in the case of insert entity, it needs to be interpretted as negative.
    //I have not found a case where $ANGDIR is negative. But if we do, I speculate we'll need to use it when interpretting
    //rotation angle for each entity.
    //const isPositiveAngle = dxf.header?.$ANGDIR === 0;

    switch (entity.type) {
      case EntityName.Solid:
        return {
          faces: this.#addSolid(entity, layers, parentTransform, parentIndex),
        };
      case EntityName.Circle:
      case EntityName.Arc:
        return {
          edges: this.#addArc(
            entity,
            lineTypes,
            layers,
            parentTransform,
            parentIndex,
          ),
        };
      case EntityName.Ellipse:
        return {
          edges: [
            this.#addEllipse(
              entity,
              lineTypes,
              layers,
              parentTransform,
              parentIndex,
            ),
          ],
        };
      case EntityName.Lwpolyline:
      case EntityName.Line:
      case EntityName.Polyline:
      case EntityName.MLine:
        return {
          edges: [
            this.#addLine(
              entity,
              lineTypes,
              layers,
              parentTransform,
              parentIndex,
            ),
          ],
        };
      case EntityName.Leader:
        return this.#addLeader(
          entity,
          lineTypes,
          layers,
          parentTransform,
          parentIndex,
        );
      case EntityName.Text:
        return {
          texts: this.#addText(
            entity,
            layers,
            styles,
            parentTransform,
            parentIndex,
          ),
        };
      case EntityName.Point:
        return {
          vertices: [
            this.#addPoint(entity, layers, parentTransform, parentIndex),
          ],
        };
      case EntityName.Insert:
        return this.#addInsert(
          entity,
          dxf,
          layers,
          parentTransform,
          parentIndex,
        );
      case EntityName.Spline:
        return {
          edges: [
            this.#addSpline(
              entity,
              lineTypes,
              layers,
              parentTransform,
              parentIndex,
            ),
          ],
        };
      case EntityName.Mtext:
        return {
          texts: this.#addMtext(
            entity,
            layers,
            styles,
            parentTransform,
            parentIndex,
          ),
        };
      case EntityName.Dimension:
        if ((entity.dimensionType & 7) === 0) {
          return this.#addDimension(
            entity,
            dxf,
            layers,
            parentTransform,
            parentIndex,
          );
        } else {
          console.warn(
            `Unsupported DXF Dimension type: ${
              (entity.dimensionType & 7) === 0
            }`,
          );
        }
        break;
      default:
        console.warn('Unsupported DXF Entity Type: ' + entity.type);
    }
    return {};
  }

  #addSolid(
    entity:
      | ISolidEntity
      | {
          type: 'leader arrow';
          points: IPoint[];
          extrusionDirection: IPoint;
          color?: number;
          layer: string;
        },
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): FaceJson[] {
    //solid is a filled triangle or quadrilateral

    const { type, color, points, extrusionDirection } = entity;
    if (!points || points.length < 3) {
      return [];
    }

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;

    const axis = this.#normalizeAxis(extrusionDirection);
    const transform = this.#arbitraryAxisAlg(axis, parentTransform);

    //TODO: The python ezdxf reader docs suggest solid is always triangle or quadrilateral.
    //But the DXF spec does not say this. I speculate that ezdxf is actually referring to
    //opengl/webgl 'gl.triangle_strip' primitive. (google 'gl.triangle_strip')
    //Today, TessJSON expects `connectivity` list and `vertices`, so this code translates
    //the points into triangle vertices (and considers swapping points to keep normal positive)
    //I think this swapping points works because it is a how a gl.triangle_strip is encoded.
    //If it is really a gl.triangle_strip, then we can handle > 4 points and shouldn't have to calculate
    //the normal and swap points if the direction is positive.
    const [p0, p1, p2, p3] = points;

    //Instantiate connectivity and vertices with first triangle
    const connectivity: [number, number, number][] = [[0, 1, 2]];
    const firstTriangle = this.#getTriangle(p0, p1, p2, transform);
    const { vertices } = firstTriangle;
    let { area } = firstTriangle;

    if (p3 && !(p2.x === p3.x && p2.y === p3.y && p2.z === p3.z)) {
      //it's a quadrilateral, so push 2nd triangle
      connectivity.push([3, 4, 5]);
      const triangle = this.#getTriangle(p1, p2, p3, transform);
      vertices.push(...triangle.vertices);
      area += triangle.area;
    }

    return [
      {
        ind: this.#nextIndex(),
        connectivity,
        vertices,
        meta: {
          type: type.toLowerCase(),
          layer: layer ?? '',
          ...(color ? { color } : null),
          area,
          ...(parentIndex && { parentIndex }),
        },
      },
    ];
  }

  #addArc<T extends ICircleEntity | IArcEntity>(
    entity: T,
    lineTypes: Record<string, ILineType> | undefined,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): EdgeJson[] {
    let { startAngle, endAngle } = entity;

    startAngle = startAngle == null ? 0 : (startAngle *= degToRad);
    endAngle = endAngle == null ? startAngle + twoPi : (endAngle *= degToRad);

    let deltaAngle = endAngle - startAngle;
    const samePoints = Math.abs(deltaAngle) < Number.EPSILON;

    // ensures that deltaAngle is 0 .. 2 PI
    while (deltaAngle < 0) deltaAngle += twoPi;
    while (deltaAngle > twoPi) deltaAngle -= twoPi;

    if (deltaAngle < Number.EPSILON) {
      if (samePoints) {
        deltaAngle = 0;
      } else {
        deltaAngle = twoPi;
      }
    }

    const { radius, center, extrusionDirection, type } = entity;

    const axis = this.#normalizeAxis(extrusionDirection);
    const transform = this.#arbitraryAxisAlg(axis, parentTransform);
    const scale = transform ? matrix4DecomposeScale(transform)[1] : 1; //using yScale because it is always positive

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;
    const lineType =
      entity.lineType && lineTypes && lineTypes[entity.lineType]
        ? lineTypes[entity.lineType]
        : undefined;

    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }

    const centerX = center.x;
    const centerY = center.y;
    const centerZ = center.z ?? 0;

    const points = this.#tesselateArc(
      radius,
      startAngle,
      deltaAngle,
      centerX,
      centerY,
      transform,
      centerZ,
    );

    const untransformedCenter: Vector3Json = [centerX, centerY, centerZ ?? 0];

    return [
      {
        ind: this.#nextIndex(),
        points,
        meta: {
          type: type.toLowerCase(),
          layer: layer ?? '',
          ...(lineType && { lineType }),
          ...(color ? { color } : null),
          axis: transform ? matrix4Vec3Multiply(transform, axis) : axis,
          length: deltaAngle * radius * scale,
          center: transform
            ? matrix4Vec3Multiply(transform, untransformedCenter)
            : untransformedCenter,
          ...(parentIndex && { parentIndex }),
        },
      },
    ];
  }

  #addEllipse(
    entity: IEllipseEntity,
    lineTypes: Record<string, ILineType> | undefined,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): EdgeJson {
    let { startAngle, endAngle } = entity;

    //Note: Unlike ICircleEntity | IArcEntity, IEllipseEntity's startAngle and endAngle are radians
    startAngle = startAngle == null ? 0 : startAngle;
    endAngle = endAngle == null ? startAngle + twoPi : endAngle;

    let deltaAngle = endAngle - startAngle;
    const samePoints = Math.abs(deltaAngle) < Number.EPSILON;

    // ensures that deltaAngle is 0 .. 2 PI
    while (deltaAngle < 0) deltaAngle += twoPi;
    while (deltaAngle > twoPi) deltaAngle -= twoPi;

    if (deltaAngle < Number.EPSILON) {
      if (samePoints) {
        deltaAngle = 0;
      } else {
        deltaAngle = twoPi;
      }
    }

    const { majorAxisEndPoint, axisRatio, center, extrusionDirection, type } =
      entity;

    const majorAxisEndPointX = majorAxisEndPoint.x;
    const majorAxisEndPointY = majorAxisEndPoint.y;

    //xRad: major radius
    const xRad = Math.sqrt(
      majorAxisEndPointX * majorAxisEndPointX +
        majorAxisEndPointY * majorAxisEndPointY,
    );
    //yRad: minor radius = major radius * ratio of minor axis to major axis
    const yRad = xRad * axisRatio;
    const rotation = Math.atan2(majorAxisEndPointY, majorAxisEndPointX);

    const centerX = center.x;
    const centerY = center.y;
    const centerZ = center.z ?? 0;

    const axis = this.#normalizeAxis(extrusionDirection);
    const transform = this.#arbitraryAxisAlg(axis, parentTransform);
    const scale = transform ? matrix4DecomposeScale(transform)[1] : 1; //using yScale because it is always positive

    //length: https://en.wikipedia.org/wiki/Ellipse#Circumference gives approximation for circumference by Ramanujan
    let h: number = (xRad - yRad) / (xRad + yRad);
    h = h * h;
    const length =
      Math.PI *
      (xRad + yRad) *
      (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h))) *
      scale;

    //In Future: consider variable tessellation based on chordError
    //For now, tessellating ellipse with 64 lines, which is fine for most ellipses for full traversal

    const divisions = Math.ceil((deltaAngle / twoPi) * 64); //round up. divisions is range 1 .. 64 given adjustments to deltaAngle above

    const points: PointJson[] = [];

    const cosRotation = Math.cos(rotation);
    const sinRotation = Math.sin(rotation);

    for (let d = 0; d <= divisions; d++) {
      let x: number;
      let y: number;
      const angle = startAngle + (deltaAngle * d) / divisions; //evenly spaced tessellations.
      //calculate the x, y components (not accounting for centerX and centerY yet)
      x = xRad * Math.cos(angle);
      y = yRad * Math.sin(angle);
      if (rotation !== 0) {
        // Rotate [x, y] about the center of the ellipse.
        [x, y] = [
          x * cosRotation - y * sinRotation,
          x * sinRotation + y * cosRotation,
        ];
      }
      x += centerX;
      y += centerY;
      const p: PointJson = [x, y, centerZ];
      const point: PointJson = transform
        ? matrix4Vec3Multiply(transform, p)
        : p;
      points.push(point);
    }

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;
    const lineType =
      entity.lineType && lineTypes && lineTypes[entity.lineType]
        ? lineTypes[entity.lineType]
        : undefined;

    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }

    const untransformedCenter: Vector3Json = [centerX, centerY, centerZ ?? 0];

    return {
      ind: this.#nextIndex(),
      points,
      meta: {
        type: type.toLowerCase(),
        layer: layer ?? '',
        ...(lineType && { lineType }),
        ...(color ? { color } : null),
        axis: transform ? matrix4Vec3Multiply(transform, axis) : axis,
        length,
        center: transform
          ? matrix4Vec3Multiply(transform, untransformedCenter)
          : untransformedCenter,
        ...(parentIndex && { parentIndex }),
      },
    };
  }

  #addLine<
    T extends
      | ILwpolylineEntity
      | ILineEntity
      | IPolylineEntity
      | IMLineEntity
      | {
          type: 'leader line';
          vertices: IPoint[];
          extrusionDirection: IPoint;
          layer: string;
          lineType: string;
          color?: number;
        },
  >(
    entity: T,
    lineTypes: Record<string, ILineType> | undefined,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): EdgeJson {
    const points: PointJson[] = [];
    const { vertices, extrusionDirection, type } = entity;

    const axis = this.#normalizeAxis(extrusionDirection);
    const transform = this.#arbitraryAxisAlg(axis, parentTransform);

    let length = 0;

    for (const [i, vertex] of vertices.entries()) {
      const bulge = (
        vertex as (ILwpolylineEntity | IPolylineEntity)['vertices'][number]
      ).bulge;
      if (bulge) {
        const startPoint: Point2Json = [vertex.x, vertex.y];
        const endPoint: Point2Json = vertices[i + 1]
          ? [vertices[i + 1].x, vertices[i + 1].y] //next point
          : [vertices[0].x, vertices[0].y]; //otherwise connect back to first point
        const [tessPoints, arcLength] = this.#getBulgeCurvePoints(
          startPoint,
          endPoint,
          bulge,
          transform,
        );
        length += arcLength;
        points.push(...tessPoints);
      } else {
        const previousPoint: PointJson | undefined = points[points.length - 1];
        const p: PointJson = [vertex.x, vertex.y, vertex.z ?? 0];
        const point: PointJson = transform
          ? matrix4Vec3Multiply(transform, p)
          : p;
        if (previousPoint) {
          length += norm(vecSub(point, previousPoint));
        }
        points.push(point);
      }
    }

    //close the shape if `shape === true`
    if (
      (entity as ILwpolylineEntity | IPolylineEntity).shape &&
      points.length
    ) {
      length += norm(vecSub(points[0], points[points.length - 1]));
      points.push(points[0]);
    }

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;
    const lineType =
      entity.lineType && lineTypes && lineTypes[entity.lineType]
        ? lineTypes[entity.lineType]
        : undefined;

    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }

    const edgeAxis: PointJson | undefined =
      type === EntityName.Line ||
      (vertices.length === 2 &&
        //If a polyline has just 2 vertices, we can define its axis. This is common for PDFs that we transform into DXF.
        //(The transformed PDF vector entities are polyline rather than line.)
        //Having this axis defined is helpful for measurements between two lines/polylines
        !(
          vertices[0] as (
            | ILwpolylineEntity
            | IPolylineEntity
          )['vertices'][number]
        ).bulge)
        ? normalize(vecSub(points[1], points[0]))
        : undefined;

    return {
      ind: this.#nextIndex(),
      points,
      meta: {
        type: type.toLowerCase(),
        ...(edgeAxis && { axis: edgeAxis }),
        layer: layer ?? '',
        length,
        ...(lineType && { lineType }),
        ...(color ? { color } : null),
        ...(parentIndex && { parentIndex }),
      },
    };
  }

  #addLeader(
    entity: ILeaderEntity,
    lineTypes: Record<string, ILineType> | undefined,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): Partial<TessAccumulator> {
    const {
      handle,
      type,
      isSpline,
      vertices,
      extrusionDirection,
      layer,
      lineType,
      hasArrowHead,
    } = entity;

    //Faces and Edges for the leader are wrapped in a definition and referenced so they can be selectable as a group
    const refId = `leader_${handle}`;
    const index = this.#nextIndex();

    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }

    const axis = this.#normalizeAxis(extrusionDirection);
    const csysTransform = this.#arbitraryAxisAlg(axis, parentTransform);

    const edges: EdgeJson[] = [
      isSpline
        ? this.#addSpline(
            {
              controlPoints: vertices,
              type: 'leader line',
              normalVector: extrusionDirection,
              degreeOfSplineCurve: 3,
              knotValues: [],
              layer,
              lineType,
              ...(color ? { color } : null),
            },
            lineTypes,
            layers,
            csysTransform,
            index, //parentIndex
          )
        : this.#addLine(
            {
              type: 'leader line',
              vertices,
              extrusionDirection,
              layer,
              lineType,
              ...(color ? { color } : null),
            },
            lineTypes,
            layers,
            csysTransform,
            index, //parentIndex
          ),
    ];

    const faces: FaceJson[] = [];
    if (hasArrowHead === undefined || hasArrowHead) {
      const start = vertices[0];
      const second = vertices[1];
      const side1 = 0.39913139058563857;
      const side2 = 0.13123359282811453;
      const _start: PointJson = [start.x, start.y, start.z ?? 0];
      const _second: PointJson = [second.x, second.y, second.z ?? 0];
      const _yDir: PointJson = normalize(vecSub(_second, _start));
      const yDir = scaleVec(side1, _yDir);
      const axis = this.#normalizeAxis(extrusionDirection);
      const xDir = scaleVec(side2 / 2, cross(_yDir, axis));
      const xNegDir = scaleVec(-1, xDir);

      //TODO: Revisit `scale` of the arrowHead. It is not clear how big to make it.
      const _transform = matrix4Compose(_start, noRotation, defaultScale);
      const transform = csysTransform
        ? matrix4Multiply(csysTransform, _transform)
        : _transform;

      const points = [
        [0, 0, 0] as PointJson,
        vecAdd(xNegDir, yDir),
        vecAdd(xDir, yDir),
      ].map((p) => {
        const _p = matrix4Vec3Multiply(transform, p);
        return { x: _p[0], y: _p[1], z: _p[2] } as IPoint;
      });

      faces.push(
        ...this.#addSolid(
          {
            type: 'leader arrow',
            extrusionDirection,
            layer,
            points,
            ...(color ? { color } : null),
          },
          layers,
          csysTransform,
          index, //parentIndex
        ),
      );
    }

    return {
      edges,
      faces,
      refs: [
        {
          ind: index,
          refId,
          transform: identityMatrix4,
          meta: {
            type: type.toLowerCase(),
            members: [
              ...faces.map<[ENTITY_TYPES.FACE, number]>((f) => [
                ENTITY_TYPES.FACE,
                f.ind,
              ]),
              ...edges.map<[ENTITY_TYPES.EDGE, number]>((e) => [
                ENTITY_TYPES.EDGE,
                e.ind,
              ]),
            ],
            layer: layer ?? '',
            ...(color && { color }),
            ...(parentIndex && { parentIndex }),
          },
        },
      ],
    };
  }

  #addText(
    entity: ITextEntity,
    layers: Record<string, ILayer> | undefined,
    styles: Record<string, IStyle> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): TextJson[] {
    if (!entity.text) {
      return [];
    }
    const {
      firstAlignment,
      secondAlignment,
      xScale,
      extrusionDirection,
      textHeight,
      thickness,
      rotation,
      halign,
      valign,
      type,
    } = entity;

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;

    const s = entity.style ?? '';
    const dxfStyle = styles && styles[s];
    const font = dxfStyle && dxfStyle.fontFileName;

    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }

    const content = parseDxfTextContent(entity.text);
    //strip out formatting: strike-through, overscore, underscore
    const text = content.map((c) => c.text).join('');

    //TODO: Add strike-through, overscore, underscore

    let size = textHeight ?? 1;
    let anchorX: TextJson['anchorX'] = 'left';
    let anchorY: TextJson['anchorY'];
    let textAlign: TextJson['textAlign'] = 'left';
    let width: TextJson['rectWidth'] = size * (xScale ?? 1) * text.length;
    const aspect = size / width;
    let insert: PointJson = [
      firstAlignment.x,
      firstAlignment.y,
      firstAlignment.z ?? 0,
    ];
    let alignPoint: PointJson = [
      secondAlignment.x,
      secondAlignment.y,
      secondAlignment.z ?? 0,
    ];
    const fitLength = norm(vecSub(alignPoint, insert));

    switch (halign) {
      case 1:
        anchorX = 'center';
        break;
      case 2:
        anchorX = 'right';
        break;
      case 3:
        if (!valign) {
          //'aligned'
          //Text is stretched or compressed to fit between firstAlignment and secondAlignment
          //And height is adjusted to preserve the height/width ratio
          width = fitLength;
          size = aspect * width;
          [insert, alignPoint] = [alignPoint, insert]; //swap
        }
        break;
      case 4:
        if (!valign) {
          //'middle'
          textAlign = 'center';
          anchorY = 'middle';
        }
        break;
      case 5:
        if (!valign) {
          //'fit'
          //Text is stretched or compressed to fit between firstAlignment and secondAlignment
          //And vertically centered (not stretched)
          width = fitLength;
          anchorY = 'middle';
          [insert, alignPoint] = [alignPoint, insert]; //swap
        }
        break;
      case 0:
      default:
        anchorX = 'left';
    }
    switch (valign) {
      case 3:
        anchorY = 'top';
        break;
      case 2:
        anchorY = 'middle';
        break;
      case 1:
        anchorY = 'bottom';
        break;
      case 0:
      default:
        //baseline (handled above halign cases 3, 4, 5 when !valign)
        anchorY = 'bottom';
    }

    const axis = this.#normalizeAxis(extrusionDirection);
    const _transform = this.#arbitraryAxisAlg(axis, parentTransform);

    const r = quaternionFromAxisAngle(axis, (rotation ?? 0) * degToRad);
    const componentTransform = matrix4Compose(alignPoint, r, defaultScale);

    const transform = _transform
      ? matrix4Multiply(_transform, componentTransform)
      : componentTransform;

    return [
      {
        ind: this.#nextIndex(),
        text,
        ...(font && { font }),
        ...(size && { size }),
        ...(width && { width }),
        ...(anchorX && { anchorX }),
        ...(anchorY && { anchorY }),
        ...(textAlign && { textAlign }),
        height: thickness ?? 0, //thickness to extrude
        transform,
        meta: {
          type: type.toLowerCase(),
          layer: layer ?? '',
          ...(color && { color }),
          ...(parentIndex && { parentIndex }),
        },
      },
    ];
  }

  #addPoint(
    entity: IPointEntity,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): VertexJson {
    const {
      position: { x, y, z },
      extrusionDirection,
    } = entity;

    const axis = this.#normalizeAxis(extrusionDirection);
    const transform = this.#arbitraryAxisAlg(axis, parentTransform);

    const _point = [x, y, z ?? 0] as PointJson;
    const point = transform ? matrix4Vec3Multiply(transform, _point) : _point;

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;
    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }
    return {
      ind: this.#nextIndex(),
      point,
      meta: {
        type: 'point',
        layer: layer ?? '',
        ...(color && { color }),
        ...(parentIndex && { parentIndex }),
      },
    };
  }

  #addInsert(
    entity: IInsertEntity,
    dxf: IDxf,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): Partial<TessAccumulator> {
    const block = dxf.blocks && dxf.blocks[entity.name];
    if (block && block.entities) {
      const {
        xScale,
        yScale,
        zScale,
        rotation,
        position,
        extrusionDirection,
        type,
      } = entity;

      const layer =
        entity.layer && layers && entity.layer in layers
          ? entity.layer
          : undefined;
      let color: number | undefined = entity.color;
      if (
        color == null &&
        layer &&
        layers &&
        layers[layer] &&
        layers[layer].color
      ) {
        color = layers[layer].color;
      }

      const axis = this.#normalizeAxis(extrusionDirection);
      const _transform = this.#arbitraryAxisAlg(axis, parentTransform);

      const angle = (-rotation ?? 0) * degToRad; //Subtle: The angle in DXF is

      const index = this.#nextIndex();

      const componentTransform = matrix4Compose(
        [position.x, position.y, position.z ?? 0], //translate
        angle ? quaternionFromAxisAngle(axis, angle) : noRotation, //rotate
        [xScale ?? 1, yScale ?? 1, zScale ?? 1], //scale
      );

      const transform = _transform
        ? matrix4Multiply(_transform, componentTransform)
        : componentTransform;

      const { faces, edges, vertices, texts, refs } =
        this.#accumulateBlockEntities(block, dxf, transform, index);

      const ref: RefJson = {
        ind: index,
        refId: entity.name,
        transform,
        meta: {
          type: type.toLowerCase(),
          members: [
            ...faces.map<[ENTITY_TYPES.FACE, number]>((f) => [
              ENTITY_TYPES.FACE,
              f.ind,
            ]),
            ...edges.map<[ENTITY_TYPES.EDGE, number]>((e) => [
              ENTITY_TYPES.EDGE,
              e.ind,
            ]),
            ...vertices.map<[ENTITY_TYPES.VERTEX, number]>((v) => [
              ENTITY_TYPES.VERTEX,
              v.ind,
            ]),
            ...texts.map<[ENTITY_TYPES, number]>((t) => [
              ENTITY_TYPES.TEXT,
              t.ind,
            ]),
            ...refs.map<[ENTITY_TYPES, number]>((r) => [
              ENTITY_TYPES.REF,
              r.ind,
            ]),
          ] as [Exclude<ENTITY_TYPES, ENTITY_TYPES.STL>, number][],
          layer: layer ?? '',
          ...(color && { color }),
          ...(parentIndex && { parentIndex }),
        },
      };

      return { faces, edges, vertices, texts, refs: [...refs, ref] };
    }
    return {};
  }

  #addSpline(
    entity:
      | ISplineEntity
      | {
          type: 'leader line';
          normalVector: IPoint;
          controlPoints: (IPoint & { weight?: number })[];
          degreeOfSplineCurve: number;
          knotValues: number[];
          lineType: string;
          layer: string;
          color?: number;
        },
    lineTypes: Record<string, ILineType> | undefined,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): EdgeJson {
    const {
      controlPoints,
      degreeOfSplineCurve,
      knotValues,
      normalVector,
      type,
    } = entity;
    const _controlPoints: PointJson[] = [];
    const weights: number[] = [];
    for (const { x, y, z, weight } of controlPoints ?? []) {
      _controlPoints.push([x, y, z ?? 0]);
      if (weight) {
        weights.push(weight);
      }
    }

    const axis = this.#normalizeAxis(normalVector);
    const transform = this.#arbitraryAxisAlg(axis, parentTransform);

    //TODO: tesselate each segment 100 times
    //* This may be overkill for some splines.
    //* It is also necessary to estimate length. (length may be calculable exactly using line integral without summing tessllation lengths)
    const [points, length] = this.#tessellateSpline(
      _controlPoints,
      degreeOfSplineCurve,
      knotValues,
      weights.length ? weights : undefined,
      transform,
      100,
    );

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;
    const lineType =
      entity.lineType && lineTypes && lineTypes[entity.lineType]
        ? lineTypes[entity.lineType]
        : undefined;
    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }
    const index = this.#nextIndex();
    return {
      ind: index,
      points,
      meta: {
        type: type.toLowerCase(),
        layer: layer ?? '',
        ...(color && { color }),
        ...(lineType && { lineType }),
        length,
        ...(parentIndex && { parentIndex }),
      },
    };
  }

  #addMtext(
    entity: IMtextEntity,
    layers: Record<string, ILayer> | undefined,
    styles: Record<string, IStyle> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): TextJson[] {
    if (!entity.text) {
      return [];
    }
    const {
      position,
      attachmentPoint,
      directionVector,
      extrusionDirection,
      height,
      rectWidth,
      rotation,
    } = entity;

    const layer =
      entity.layer && layers && entity.layer in layers
        ? entity.layer
        : undefined;

    const s = entity.style ?? '';
    const dxfStyle = styles && styles[s];
    let font = dxfStyle && dxfStyle.fontFileName;

    let color: number | undefined = entity.color;
    if (
      color == null &&
      layer &&
      layers &&
      layers[layer] &&
      layers[layer].color
    ) {
      color = layers[layer].color;
    }

    const textAndControlChars: DxfMTextContentElement[] = parseDxfMTextContent(
      entity.text,
    );
    const { text, style } = this.#mtextContentAndFormattingToTextAndStyle(
      textAndControlChars,
      entity,
      color ?? 0,
    );

    let anchorX: TextJson['anchorX'];
    let anchorY: TextJson['anchorY'];

    //If the style encoded in textAndControlChars defines a fontFamily for the whole range of the text then use it
    //Note: we currently don't support multiple fonts in mtext's text
    const { fontFamily } = style;
    if (fontFamily.length === 1) {
      const [f, start, end] = fontFamily[0];
      if (start === 0 && end === text.length - 1) {
        font = f;
      }
    }

    switch (attachmentPoint) {
      case 1:
        // Top Left
        anchorX = 'left';
        anchorY = 'top';
        break;
      case 2:
        // Top Center
        anchorX = 'center';
        anchorY = 'top';
        break;
      case 3:
        // Top Right
        anchorX = 'right';
        anchorY = 'top';
        break;

      case 4:
        // Middle Left
        anchorX = 'left';
        anchorY = 'middle';
        break;
      case 5:
        // Middle Center
        anchorX = 'center';
        anchorY = 'middle';
        break;
      case 6:
        // Middle Right
        anchorX = 'right';
        anchorY = 'middle';
        break;

      case 7:
        // Bottom Left
        anchorX = 'left';
        anchorY = 'bottom';
        break;
      case 8:
        // Bottom Center
        anchorX = 'center';
        anchorY = 'bottom';
        break;
      case 9:
        // Bottom Right
        anchorX = 'right';
        anchorY = 'bottom';
        break;
    }

    const axis = this.#normalizeAxis(extrusionDirection);
    const _transform = this.#arbitraryAxisAlg(axis, parentTransform);

    let zRot: number = (rotation ?? 0) * degToRad;
    if (directionVector) {
      //angle from x axis to directionVector (WCS)
      zRot = angleTo(xAxis, [
        directionVector.x,
        directionVector.y,
        directionVector.z ?? 0,
      ]);
    }

    const r = zRot ? quaternionFromAxisAngle(axis, zRot) : noRotation;

    const componentTransform = matrix4Compose(
      [position.x, position.y, position.z ?? 0],
      r,
      defaultScale,
    );

    const transform = _transform
      ? matrix4Multiply(_transform, componentTransform)
      : componentTransform;

    let size = height ?? style.textHeight; //size of text

    if (style.scale) {
      //Hack scaling all text rather than subset. In most cases it seems it applies to all anyways, so this works
      size *= style.scale;
    }

    const _text = text.replaceAll('\\P', '\n').replaceAll('\\X', '\n');

    return [
      {
        ind: this.#nextIndex(),
        text: _text,
        ...(font && { font }),
        ...(size && { size }),
        ...(rectWidth && { rectWidth }),
        ...(anchorX && { anchorX }),
        ...(anchorY && { anchorY }),
        textAlign: style.textAlign,
        transform,
        meta: {
          type: 'mtext',
          layer: layer ?? '',
          ...(color && { color }),
          ...(parentIndex && { parentIndex }),
        },
      },
    ];
  }

  #addDimension(
    entity: IDimensionEntity,
    dxf: IDxf,
    layers: Record<string, ILayer> | undefined,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): Partial<TessAccumulator> {
    const block = dxf.blocks && dxf.blocks[entity.block];

    if (block && block.entities) {
      const layer =
        entity.layer && layers && entity.layer in layers
          ? entity.layer
          : undefined;
      let color: number | undefined = entity.color;
      if (
        color == null &&
        layer &&
        layers &&
        layers[layer] &&
        layers[layer].color
      ) {
        color = layers[layer].color;
      }

      const index = this.#nextIndex();

      //It is strange, but dimensions are always just the incoming parentTransform
      //Contrast this to insert entity, where the transform is the product of parentTransform and the
      //unlike insert entity, it doesn't apply #arbitraryAxisAlg or the componentTransform
      const { faces, edges, vertices, texts, refs } =
        this.#accumulateBlockEntities(block, dxf, parentTransform, index);

      const ref: RefJson = {
        ind: index,
        refId: entity.block,
        transform: identityMatrix4,
        meta: {
          type: 'dimension',
          members: [
            ...faces.map<[ENTITY_TYPES.FACE, number]>((f) => [
              ENTITY_TYPES.FACE,
              f.ind,
            ]),
            ...edges.map<[ENTITY_TYPES.EDGE, number]>((e) => [
              ENTITY_TYPES.EDGE,
              e.ind,
            ]),
            ...vertices.map<[ENTITY_TYPES.VERTEX, number]>((v) => [
              ENTITY_TYPES.VERTEX,
              v.ind,
            ]),
            ...texts.map<[ENTITY_TYPES, number]>((t) => [
              ENTITY_TYPES.TEXT,
              t.ind,
            ]),
            ...refs.map<[ENTITY_TYPES, number]>((r) => [
              ENTITY_TYPES.REF,
              r.ind,
            ]),
          ] as [Exclude<ENTITY_TYPES, ENTITY_TYPES.STL>, number][],
          layer: layer ?? '',
          ...(color && { color }),
          ...(parentIndex && { parentIndex }),
        },
      };

      return { faces, edges, vertices, texts, refs: [...refs, ref] };
    }
    return {};
  }

  #getBulgeCurvePoints(
    [startX, startY]: Point2Json,
    [endX, endY]: Point2Json,
    bulge: number,
    transform?: Matrix4Json | null,
  ): [PointJson[], number] {
    //* The curvature of a Polyline Arc segment is defined using a quantity known as bulge.
    //  This unit measures the deviation of the curve from the straight line (chord) joining the two vertices of the segment.
    //* It is defined as the ratio of the arc sagitta (versine) to half the length of the chord between the two vertices; this ratio
    //  is equal to the tangent of a quarter of the included arc angle between the two polyline vertices.
    //* A negative bulge indicates that the arc follows a clockwise direction from the first vertex to the next, with a positive bulge
    //  describing an anticlockwise oriented arc. A bulge of 0 indicates a straight segment, and a bulge of 1 is a semicircle.
    //* An AutoCAD Arc Entity is defined by a center, radius and start & end angle. The arc is always defined to be anticlockwise oriented,
    //  that is, following an anticlockwise direction from the start angle to the end angle.

    const halfTheta = 2 * Math.atan(bulge);
    const theta = 2 * halfTheta;

    //Calculate chord length; the distance between start and end. This is defined as 2*d
    //Note: chord is same as `distanceTo(startPoint, endPoint)` from geom-utils.ts
    const x = endX - startX;
    const y = endY - startY;
    const chord = Math.sqrt(x * x + y * y);
    const d = chord / 2; //half distance of chord

    const radius = d / Math.sin(halfTheta); //radius of arc
    //const sagitta = bulge * d; //height of the arc above the chord
    //const apothem = radius - sagitta; //magnitude of distance from chord to center

    //chord projected onto x axis is `dotProduct([x, y], [1, 0]) / chord` === `x / chord`, therefore chord's angle from x axis is acos(x/chord)
    let angleFromXaxisToChord = Math.acos(x / chord);
    if (y < 0) angleFromXaxisToChord = -angleFromXaxisToChord;

    //angleFromChordToCenterPoint = 90 degress - halfTheta (right hand triangle and sum of angles of triangle === 180)
    const angleFromChordToCenterPoint = halfPi - halfTheta;

    const angleFromXaxisToCenterPoint =
      angleFromXaxisToChord + angleFromChordToCenterPoint;

    //Now center of the bulge's arc can be calculated
    const centerX = startX + radius * Math.cos(angleFromXaxisToCenterPoint);
    const centerY = startY + radius * Math.sin(angleFromXaxisToCenterPoint);

    const centerToStartX = startX - centerX;
    const centerToStartY = startY - centerY;
    const centerToStart = Math.sqrt(
      centerToStartX * centerToStartX + centerToStartY * centerToStartY,
    );

    //centerToStart projected onto x axis is `dotProduct([centerToStartX, centerToStartY], [1, 0]) / centerToStart` === `centerToStartX / centerToStart`
    //therefore startAngle (from x axis) is acos(centerToStartX/centerToStart)
    let startAngle = Math.acos(centerToStartX / centerToStart);
    if (centerToStartY < 0) startAngle = -startAngle;

    const vertices = this.#tesselateArc(
      radius,
      startAngle,
      theta,
      centerX,
      centerY,
      transform,
    );

    const scale = transform ? matrix4DecomposeScale(transform)[1] : 1;
    const arcLength = radius * theta * scale; //arcLength of bulged arc between startPoint and endPoint

    return [vertices, arcLength];
  }

  #tesselateArc(
    radius: number,
    startAngle: number,
    deltaAngle: number,
    centerX: number,
    centerY: number,
    transform?: Matrix4Json | null,
    centerZ = 0,
  ): PointJson[] {
    // Notes about arc tessellation:
    //   chordError = r - l, where l is the the is perpendicular distance from the chord to the center of the arc/circle
    //   l = r * cos(theta / 2)
    //   So theta = 2 * acos(1 - (chordError / r))
    // Assume tessellating a circle into 32 pieces is acceptable tessellation, theta = 11.25 degrees and halfTheta = 5.625 degrees
    // which gives l / r ~ 0.995. So at most the position error is ~ 0.5% of radius when 32 divisions is used.
    // Similarly, for 64 pieces, l / r = 0.99969... and position error is ~ 0.031 % of radius
    // So the chosen divisions is based on tessellating a full circle with 64 segments.
    // This is for visual graphical tessllation.  This should not be used for manufacturing. `0.031% * radius` accuracy is probably not enough.
    // (For manufacturing it should have higher tesselation or better, send a G02/G03 code to CNC control to traverse the arc.)
    const divisions = Math.ceil((deltaAngle / twoPi) * 64); //round up. divisions is range 1 .. 64 given adjustments to deltaAngle above

    const points: PointJson[] = [];
    for (let d = 0; d <= divisions; d++) {
      const angle = startAngle + (deltaAngle * d) / divisions; //evenly spaced tessellations. (May be slightly < 5.625 degrees given Math.ceil())
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      const p: PointJson = [x, y, centerZ];
      const point: PointJson = transform
        ? matrix4Vec3Multiply(transform, p)
        : p;
      points.push(point);
    }
    return points;
  }

  // Interpolate a b-spline. The algorithm examines the knot vector
  // to create segments for interpolation. The parameterized value
  // is re-normalised back to [0,1] as required by bspline()
  // (t is de-normalised in bspline())
  #tessellateSpline(
    controlPoints: PointJson[],
    degree: number,
    knots: number[],
    weights: number[] | undefined,
    transform: Matrix4Json | undefined | null, //normal vector (defined if spline is planar and not in plane with normal [0, 0, 1])
    interpolationsPerSplineSegment: number,
  ): [PointJson[], number] {
    const polyline: PointJson[] = [];

    const segmentTs: number[] =
      knots && degree < knots.length ? [knots[degree]] : [];

    const domain: [number, number] = [
      knots && degree < knots.length ? knots[degree] : 0,
      knots && knots.length - 1 - degree < knots.length
        ? knots[knots.length - 1 - degree]
        : 1,
    ];

    for (let k = degree + 1; k < knots.length - degree; ++k) {
      if (segmentTs[segmentTs.length - 1] !== knots[k]) {
        segmentTs.push(knots[k]);
      }
    }

    //In Future: consider variable tessellation based on chordError
    //For now, tessellating spline using 100 interpolations per segment of spline
    //This is necessary to give decent rendering and also to give decent approximation for length

    let length = 0;

    for (let i = 1; i < segmentTs.length; ++i) {
      const uMin = segmentTs[i - 1];
      const uMax = segmentTs[i];
      for (let k = 0; k <= interpolationsPerSplineSegment; ++k) {
        const u = (k / interpolationsPerSplineSegment) * (uMax - uMin) + uMin;
        // Clamp t to 0, 1 to handle numerical precision issues
        let t = (u - domain[0]) / (domain[1] - domain[0]);
        t = Math.max(t, 0);
        t = Math.min(t, 1);
        const p = bspline(
          t,
          degree,
          controlPoints,
          knots && knots.length ? knots : undefined,
          weights,
        ) as PointJson;
        const point: PointJson = transform
          ? matrix4Vec3Multiply(transform, p)
          : p;

        //TODO: currently length is approximated!
        const prevPoint = polyline[polyline.length - 1];
        if (prevPoint) {
          length += norm(vecSub(point, prevPoint));
        }

        polyline.push(point);
      }
    }
    return [polyline, length]; //interpolated points of spline (25 per segment)
  }

  #mtextContentAndFormattingToTextAndStyle(
    textAndControlChars: DxfMTextContentElement[],
    entity: IMtextEntity,
    color: number,
  ): { text: string; style: ActiveStyle } {
    const activeStyle: ActiveStyle = {
      textAlign: 'left',
      textHeight: entity.height,
      fontFamily: [],
    };

    const text = [];
    for (const item of textAndControlChars) {
      if (typeof item === 'string') {
        if (item.startsWith('pxq') && item.endsWith(';')) {
          if (item.indexOf('c') !== -1) activeStyle.textAlign = 'center';
          else if (item.indexOf('l') !== -1) activeStyle.textAlign = 'left';
          else if (item.indexOf('r') !== -1) activeStyle.textAlign = 'right';
          else if (item.indexOf('j') !== -1) activeStyle.textAlign = 'justify';
        } else if (item.startsWith('px') && item.endsWith(';')) {
          //hack: attempt to parse pxsm string to make text smaller
          //TODO: Need to come back to properly parsing this scaling infomation. Right now it's just guessing/experimenting and making it look ok
          //It is not documented in https://adndevblog.typepad.com/autocad/2017/09/dissecting-mtext-format-codes.html
          const scaleStringMatches = item.match(/pxsm(\d+\.\d*);/);
          if (scaleStringMatches) {
            const scale = parseFloat(scaleStringMatches[1]);
            if (scale && scale < 1 && scale > 0) {
              activeStyle.scale = scale * 0.8;
            }
          }
        } else {
          text.push(item);
          if (activeStyle.fontFamily.length) {
            //Update the end index of last entry
            const lastFontFamily =
              activeStyle.fontFamily[activeStyle.fontFamily.length - 1];
            const textLength: number = text.reduce((s, t) => s + t.length, 0);
            lastFontFamily[2] = lastFontFamily[2] + textLength - 1;
          }
        }
      } else if (Array.isArray(item)) {
        const nestedFormat = this.#mtextContentAndFormattingToTextAndStyle(
          item,
          entity,
          color,
        );
        text.push(nestedFormat.text);
        nestedFormat.style.fontFamily.push(...nestedFormat.style.fontFamily);
      } else if (typeof item === 'object') {
        if (item.S && item.S.length === 3) {
          text.push(item.S[0] + '/' + item.S[2]);
        } else if (item.f && typeof item.f === 'string') {
          const textLength: number = text.reduce((s, t) => s + t.length, 0);
          activeStyle.fontFamily.push([item.f, textLength, textLength]);
        }
      }
    }
    return {
      text: text.join(''),
      style: activeStyle,
    };
  }

  #normalizeAxis(axis: IPoint): PointJson {
    if (axis) {
      const x = axis.x;
      const y = axis.y;
      const z = axis.z ?? 0;
      if (!(x === 0 && y === 0 && z === 0)) {
        //only normalize if not the null vector
        return normalize([x, y, z]);
      }
    }
    return zAxis; //default axis
  }

  #accumulateBlockEntities(
    block: IBlock,
    dxf: IDxf,
    parentTransform?: Matrix4Json,
    parentIndex?: number,
  ): TessAccumulator {
    const acc: TessAccumulator = {
      faces: [],
      edges: [],
      vertices: [],
      texts: [],
      refs: [],
    };
    for (const entity of block.entities ?? []) {
      const { faces, edges, vertices, texts, refs } = this.#addEntity(
        entity,
        dxf,
        parentTransform,
        parentIndex,
      );
      acc.faces.push(...(faces ?? []));
      acc.edges.push(...(edges ?? []));
      acc.vertices.push(...(vertices ?? []));
      acc.texts.push(...(texts ?? []));
      acc.refs.push(...(refs ?? []));
    }

    return acc;
  }

  //See "Arbitrary Axis Algorithm" autocad_2012_pdf_dxf-reference_enu.pdf
  //Returns the coordinate transform from the component to world coordinate system
  #arbitraryAxisAlg(
    n: Vector3Json,
    parentTransform?: Matrix4Json,
  ): Matrix4Json | undefined {
    const _n = normalize(n);
    const [nx, ny, nz] = _n;
    if (nx === 0 && ny === 0 && nz === 1) {
      return parentTransform; //result is identity matrix, so no need to have transform
    }
    const x: Vector3Json = cross(
      Math.abs(nx) < polorCapBounds && Math.abs(ny) < polorCapBounds
        ? yAxis
        : zAxis,
      _n,
    );
    const y: Vector3Json = cross(_n, x);

    //prettier-ignore
    const csysTransform: Matrix4Json = [
      x[0], y[0], nx, 0,
      x[1], y[1], ny, 0,
      x[2], y[2], nz, 0,
         0,    0,  0, 1,

    ];

    return parentTransform
      ? csysTransform
        ? matrix4Multiply(parentTransform, csysTransform)
        : parentTransform
      : csysTransform;
  }

  #pointToVector3Json({ x, y, z }: IPoint): Vector3Json {
    return [x, y, z ?? 0];
  }

  #getTriangle(
    _p0: IPoint,
    _p1: IPoint,
    _p2: IPoint,
    transform?: Matrix4Json,
  ): { vertices: [Vector3Json, Vector3Json, Vector3Json]; area: number } {
    const p0 = this.#pointToVector3Json(_p0);
    const p1 = this.#pointToVector3Json(_p1);
    const p2 = this.#pointToVector3Json(_p2);
    const [, , normalZ] = cross(vecSub(p1, p0), vecSub(p2, p0));
    const triangle: [Vector3Json, Vector3Json, Vector3Json] =
      normalZ < 0 ? [p2, p1, p0] : [p0, p1, p2];
    const vertices: [Vector3Json, Vector3Json, Vector3Json] = transform
      ? (triangle.map((p) => matrix4Vec3Multiply(transform, p)) as [
          Vector3Json,
          Vector3Json,
          Vector3Json,
        ])
      : triangle;
    //Could probably optimize area calcuation to use previous cross() product calculation and scale decomposed from `transform`
    const area =
      Math.abs(
        norm(
          cross(
            vecSub(vertices[1], vertices[0]),
            vecSub(vertices[2], vertices[0]),
          ),
        ),
      ) / 2;
    return { vertices, area };
  }
})();
